import React, { useState } from "react";
import { X, ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const ROLES = [
  "I run a business",
  "Therapist / counsellor / coach",
  "Home baker / maker / market trader",
  "Café / hospitality / micro-retail",
  "Residents' Association / community group",
  "Sole trader on the road (gardener, cleaner, courier…)",
  "Book club / society / committee",
  "Curious — I'm a builder, designer, or investor",
  "Other",
];

/**
 * Reusable lead-capture modal.
 * Pass `source` to tag the lead origin (e.g. "interest_orderjar", "home_hero").
 * `title` and `body` are optional copy overrides.
 */
export default function InterestModal({ open, onClose, source = "interest", demoTitle, title, body, ctaLabel = "Register interest" }) {
  const [form, setForm] = useState({ name: "", email: "", role: "", consent_privacy: true });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) { toast.error("Email required"); return; }
    setSubmitting(true);
    try {
      await api.post("/leads", {
        name: form.name || "(no name)",
        email: form.email,
        role: form.role,
        notes: demoTitle ? `Interested in: ${demoTitle}` : "",
        source,
        consent_privacy: true,
        consent_marketing: true,
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save — try again?");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="interest-modal">
      <div className="absolute inset-0 bg-zinc-950/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white max-w-md w-full p-7 md:p-8 border border-zinc-200 shadow-2xl">
        <button onClick={onClose} className="absolute top-3 right-3 text-zinc-400 hover:text-zinc-900 p-1" data-testid="interest-modal-close" aria-label="Close">
          <X size={18} weight="bold" />
        </button>

        {done ? (
          <div className="py-4 text-center" data-testid="interest-modal-done">
            <CheckCircle size={40} weight="fill" className="mx-auto text-[hsl(var(--appistry-signal))] mb-4" />
            <h3 className="text-2xl font-black tracking-tighter mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>You're on the list.</h3>
            <p className="text-sm text-zinc-600 mb-6">We'll be in touch within a few working days. No spam, no sales calls — promise.</p>
            <button onClick={() => { setDone(false); onClose(); }} className="btn-ghost-ink text-sm" data-testid="interest-modal-close-done">Close</button>
          </div>
        ) : (
          <>
            <p className="overline mb-3 text-[hsl(var(--appistry-signal))]">{title || (demoTitle ? `Interested in ${demoTitle}?` : "Get on the list")}</p>
            <h3 className="text-2xl font-black tracking-tighter leading-[1.05] mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {body || "Drop your email — we'll let you know when this is ready."}
            </h3>
            <p className="text-sm text-zinc-600 mb-5">No spam, no sales calls. Just a polite update when there's something real to share.</p>

            <form onSubmit={submit} className="space-y-3" data-testid="interest-modal-form">
              <input
                type="text"
                placeholder="Your name (optional)"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full border border-zinc-200 px-3 py-2.5 text-sm focus:outline-none focus:border-zinc-900"
                data-testid="interest-modal-name"
              />
              <input
                type="email"
                required
                placeholder="you@example.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full border border-zinc-200 px-3 py-2.5 text-sm focus:outline-none focus:border-zinc-900"
                data-testid="interest-modal-email"
              />
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full border border-zinc-200 px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-zinc-900"
                data-testid="interest-modal-role"
              >
                <option value="">What do you do? (optional)</option>
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <button type="submit" disabled={submitting} className="btn-signal w-full justify-center" data-testid="interest-modal-submit">
                {submitting ? "Sending…" : ctaLabel} <ArrowRight size={14} weight="bold" />
              </button>
              <p className="text-[11px] text-zinc-500 text-center pt-1">By registering you agree to our <a href="/privacy" className="underline">Privacy Policy</a>.</p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
